import React, { useState } from 'react';
import ConfirmationPage, { FormState } from '../../ConfirmationPage';
import FileUpload from './FileUpload';
import { SnapIcon } from '../../SnapIcon';
import { convertFileToBase64 } from '../utils';
import OrgsService from '../../../services/orgs';
import { uploadDocument } from '../../../services/vault/document';
import { StripeEnv } from '../../../util/enums';
import useIsMobile from '../hooks/useIsMobile';
import logger from '../../../util/logger';

export default function EntityDocumentationComponent({
  stripeConnectAccountId,
  gateway,
  stripeEnv = StripeEnv.RAISE,
  onSuccess,
}: {
  stripeConnectAccountId: string;
  gateway: VaultProvider;
  stripeEnv?: StripeEnv;
  onSuccess?: () => void;
}): JSX.Element {
  const [selectedDoc, setSelectedDoc] = useState<File>();
  const [submitState, setSubmitState] = useState<FormState>();
  const [submitErrors, setSubmitErrors] = useState<ApiError[]>([]);
  const [showRequiredMessage, setShowRequiredMessage] = useState(false);
  const isMobile = useIsMobile();

  const onSubmit = async (): Promise<void> => {
    if (!selectedDoc) {
      setShowRequiredMessage(true);
      return;
    }

    setSubmitState('waiting');
    if (selectedDoc) {
      const { base64String: documentFront, fileType } =
        await convertFileToBase64(selectedDoc);
      const financialAccount = await OrgsService.financialAcctId(
        stripeConnectAccountId,
      );
      if (financialAccount?.kybId) {
        try {
          await uploadDocument({
            stripeEnv,
            gateway,
            documentFront,
            type: 'certificate_of_incorporation',
            vaultId: financialAccount.kybId,
            stripeConnectAccountId,
            fileType,
          });
          setSubmitState('success');
          if (onSuccess !== undefined) {
            logger('VerifyAccountForm EntityDocumentation success');
            onSuccess();
          }
        } catch (e) {
          setSubmitState('error');
          const errors = e as string[];
          setSubmitErrors(
            errors.map((error) => ({
              message: error,
              details: JSON.stringify(e),
            })),
          );
        }
      }
    }
  };

  return (
    <React.Fragment>
      {submitState ? (
        <ConfirmationPage currentState={submitState} errors={submitErrors} />
      ) : (
        <div>
          <div className='flex items-center my-2'>
            {selectedDoc ? (
              <SnapIcon icon={'check-circle'} color={'#10B981'} size='md' />
            ) : (
              <SnapIcon icon={'exclamation'} color={'#F59E0B'} size='md' />
            )}
            <p className='mx-1 text-lg font-medium tracking-tight text-gray-900 text-center'>
              Entity Documentation
            </p>
          </div>
          <p className='my-2 mb-3 font-normal'>
            Depending on the legal structure of your entity, please provide
            proof of organization documentation such as:
          </p>
          <ul className='list-disc ml-4'>
            <li className='font-normal'>501C3 determination letter</li>
            <li className='font-normal'>DBA registration</li>
            <li className='font-normal'>SS4 EIN assignment letter</li>
          </ul>
          <FileUpload
            onFileChange={(f): void => {
              setSelectedDoc(f);
              setShowRequiredMessage(false);
            }}
            onRemoveCurrentSelection={(): void => setSelectedDoc(undefined)}
            showRequiredMessage={showRequiredMessage}
          />
          <div className={!isMobile ? 'flex justify-end' : ''}>
            <button
              onClick={onSubmit}
              className='my-8 text-white text-lg lg rounded-full bg-blue-100 w-full'
            >
              Submit
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
}
